import React from 'react';

import { css } from '@emotion/react';
import { TextButton, Type } from '@robinhood/design';
import { NeutralColorToken } from '@robinhood/design';
import { E164ToNational } from '@x1/util-format';
import { graphql } from 'relay-runtime';

import LandlineModal from './LandlineModal';
import logger from 'src/clients/LoggingClient';
import { ConfirmationPageLandlineMutation as ConfirmationPageLandlineMutationType } from 'src/clients/Network/gql/build/ConfirmationPageLandlineMutation.graphql';
import { ConfirmationPageMutation as ConfirmationPageMutationType } from 'src/clients/Network/gql/build/ConfirmationPageMutation.graphql';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import TextInput from 'src/components/creditApplication/TextInput';
import { showGlobalErrorAlert, useGlobalStore } from 'src/state/global';
import { useAsyncMutation } from 'src/utils/graphql';

const ConfirmationPageMutation = graphql`
  mutation ConfirmationPageMutation($creditApplicationId: String!, $e164: String!, $code: String!) {
    fraudAlertChallengeSubmit(q: { creditApplicationId: $creditApplicationId, e164: $e164, code: $code }) {
      success
    }
  }
`;

const ConfirmationPageLandlineMutation = graphql`
  mutation ConfirmationPageLandlineMutation($creditApplicationId: String!) {
    fraudAlertChallengeManualReview(q: { creditApplicationId: $creditApplicationId }) {
      success
    }
  }
`;

interface PropsIface {
  e164?: string;
  onLandlineNext: () => void;
}

const ConfirmationPage = ({ e164, onLandlineNext }: PropsIface) => {
  const { underwrite, underwriteLoading } = React.useContext(NavigationContext);
  const creditApplicationId = useGlobalStore((s) => s.creditApplicationId);
  const [code, setCode] = React.useState<string>('');
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [commitChallenge, challengeLoading] = useAsyncMutation<ConfirmationPageMutationType>(ConfirmationPageMutation);
  const [commitLandline, landlineLoading] = useAsyncMutation<ConfirmationPageLandlineMutationType>(
    ConfirmationPageLandlineMutation,
  );
  const loading = challengeLoading || underwriteLoading || landlineLoading;

  const buttonState = React.useMemo(() => {
    return loading ? 'loading' : code.length === 4 ? 'default' : 'disabled';
  }, [code, loading]);

  const onChangeCode = React.useCallback(({ value }: { value: string }) => {
    const formatted = value.replace(/\D/g, '');
    setCode(formatted);
  }, []);

  const onShowModal = React.useCallback(() => {
    setShowModal(true);
  }, []);

  const onDismissModal = React.useCallback(() => {
    setShowModal(false);
  }, []);

  const onSubmit = React.useCallback(async () => {
    try {
      if (!!creditApplicationId && !!e164) {
        const res = await commitChallenge({
          variables: {
            creditApplicationId,
            e164,
            code,
          },
        });
        if (res.fraudAlertChallengeSubmit.success) {
          await underwrite();
        } else {
          showGlobalErrorAlert();
        }
      }
    } catch (e) {
      logger.error(e, { source: 'FraudAlertConfirmationPage.onSubmit' });
      showGlobalErrorAlert();
    }
  }, [underwrite, commitChallenge, creditApplicationId, e164, code]);

  const onLandline = React.useCallback(async () => {
    try {
      if (!!creditApplicationId) {
        const res = await commitLandline({
          variables: {
            creditApplicationId,
          },
        });
        if (res.fraudAlertChallengeManualReview.success) {
          onLandlineNext();
        } else {
          showGlobalErrorAlert();
        }
      }
    } catch (e) {
      showGlobalErrorAlert();
    }
  }, [creditApplicationId, commitLandline, onLandlineNext]);

  return (
    <CreditApplicationPageLayout
      showLogo={false}
      title='Confirm your fraud alert phone&nbsp;number'
      subtitle={
        e164 ? `We texted a code to ${E164ToNational(e164)}.` : 'We texted a code to your fraud alert phone number.'
      }
    >
      <div css={root}>
        <TextInput
          placeholder='Code'
          value={code}
          onChange={onChangeCode}
          inputMode='numeric'
          autoComplete='one-time-code'
          maxLength={4}
        />
        <div css={footer}>
          <Type.Text size='S'>
            Can’t receive texts?{' '}
            <TextButton
              label='Help'
              variant='inline'
              type='button'
              color={NeutralColorToken.Fg1}
              onClick={onShowModal}
            />
          </Type.Text>
          <Button label='Verify' state={buttonState} width='full' type='submit' onClick={onSubmit} />
        </div>
      </div>
      {showModal && <LandlineModal e164={e164} onLandline={onLandline} onDismiss={onDismissModal} />}
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
`;

const footer = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  text-align: center;
`;

export default ConfirmationPage;
